function linkEndId(end: string | GraphNode): string | undefined {
  return typeof end === 'string' ? end : end?.id
}

function rangesEqual(a?: [number, number][], b?: [number, number][]): boolean {
  if (!a && !b) return true
  if (!a || !b || a.length !== b.length) return false
  return a.every(([s, e], i) => b[i][0] === s && b[i][1] === e)
}

export function applySearchFilter(
  nodeMap: Map<string, GraphNode>,
  linkMap: Map<string, GraphLink>,
  query: string
): void {
  const normalized = query.trim().toLowerCase()

  if (!normalized) {
    nodeMap.forEach(node => {
      if (node.matchRanges) {
        delete node.matchRanges
        node.stale = true
      }
    })
    return
  }

  const matches = new Set<string>()
  const matchedServiceParents = new Set<string>()
  nodeMap.forEach(node => {
    const label = node.label ?? ''
    const index = label.toLowerCase().indexOf(normalized)
    if (index !== -1) {
      matches.add(node.id)
      const next: [number, number][] = [[index, index + normalized.length]]
      if (!rangesEqual(node.matchRanges, next)) {
        node.matchRanges = next
        node.stale = true
      }
    } else if (node.matchRanges) {
      delete node.matchRanges
      node.stale = true
    }
  })

  // ⚠⚠ A NODE CAN HAVE SEVERAL PARENTS AND THE WALK BELOW MUST TAKE EVERY ONE (app#299). A cloud
  // location is ONE graph node with a link from each network that has a node in it, so keeping
  // only the first source made a search for it show ONE of those networks and delete the others.
  //
  // ⚠ `parentOf` stays, and stays FIRST-WINS: the service-expansion pass below wants a single
  // parent to attribute a match to, not a set.
  const parentOf = new Map<string, string>()
  const parentsOf = new Map<string, string[]>()
  linkMap.forEach(link => {
    const source = linkEndId(link.source)
    const target = linkEndId(link.target)
    if (!source || !target) return
    if (!parentOf.has(target)) parentOf.set(target, source)
    const parents = parentsOf.get(target) ?? []
    if (!parents.includes(source)) parents.push(source)
    parentsOf.set(target, parents)
  })

  // Breadth-first over the parents of every match, with `visible` as the seen set — so a cycle
  // in the link graph terminates instead of looping, exactly as the single-parent walk did.
  const visible = new Set<string>(matches)
  const pending = Array.from(matches)
  while (pending.length) {
    const id = pending.shift() as string
    ;(parentsOf.get(id) ?? []).forEach(parent => {
      if (visible.has(parent)) return
      visible.add(parent)
      pending.push(parent)
    })
  }

  Array.from(nodeMap.keys()).forEach(id => {
    if (!visible.has(id)) nodeMap.delete(id)
  })

  Array.from(linkMap.entries()).forEach(([id, link]) => {
    const source = linkEndId(link.source)
    const target = linkEndId(link.target)
    if (!source || !target || !nodeMap.has(source) || !nodeMap.has(target)) {
      linkMap.delete(id)
    }
  })

  matches.forEach(id => {
    const node = nodeMap.get(id)
    if (node?.type === 'service') {
      if (!node.expanded) {
        node.expanded = true
        node.stale = true
      }
      if (node.hideLabel) {
        node.hideLabel = false
        node.stale = true
      }
      const parentId = parentOf.get(id)
      if (parentId) matchedServiceParents.add(parentId)
    }
  })

  linkMap.forEach(link => {
    const source = linkEndId(link.source)
    const target = linkEndId(link.target)
    if (source && matchedServiceParents.has(source) && target && matches.has(target)) {
      if (!link.expanded) {
        link.expanded = true
        link.stale = true
      }
    }
  })

  nodeMap.forEach(node => {
    if (node.hideLabel) {
      node.hideLabel = false
      node.stale = true
    }
  })
}

export function visibleIdsSignature(nodeMap: Map<string, GraphNode>): string {
  return Array.from(nodeMap.keys()).sort().join('|')
}
